import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@livemaps/i18n';
import { Translations } from '@app/core/services/i18n/translations.service';
import { LocalizationService } from './localization.service';
import { filter, takeUntil, withLatestFrom } from 'rxjs/operators';
import { firstValueFrom, Subject } from 'rxjs';
import * as moment from 'moment-timezone';
import { DeviceType } from '@app/modules/location-client/location-api.models';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { DISTANCE_UNITS } from '@environments/shared.models';
import { DistanceUnits, PathDistanceUnits, PathSpeedUnits } from '@app/modules/location/models/path-api.model';
import { ReverseGeocoderData } from '@app/modules/reverse-geocoder/services/models/reverse-geocoder-response.model';
import {
  KM_PER_METER,
  KPH_PER_MM_SEC,
  MILES_PER_KM_H,
  MILES_PER_METER,
  MPH_PER_MM_SEC,
  validGpsData
} from '@app/modules/shared/utilities/utilities';
import { GetLocaleService } from './get-locale.service';
import { SettingsFacade } from '@app/modules/settings/facade/settings.facade';
import { Settings } from '@app/store/settings/reducers/settings.reducer';
import { AppConfigService } from './app-config/app-config.service';

enum ZTRAK_BATTERY_CONDITION {
  'GOOD',
  'LOW',
  'CRITICALLY_LOW'
}
@Injectable({
  providedIn: 'root'
})
export class UiUtilities implements OnDestroy {
  constructor(
    private translateService: TranslateService,
    private translations: Translations,
    private getLocaleSevice: GetLocaleService,
    private settingsFacade: SettingsFacade,
    private appConfigService: AppConfigService
  ) {
    this.settingsFacade.getDisplaySettings().subscribe(settings => {
      this.settings = settings;
    });
    this.translations.translationsLoadState
      .pipe(filter(loadstate => loadstate === ResourceLoadState.LOAD_SUCCESSFUL))
      .subscribe(() => {
        this.translated = this.translateService.instant([
          this.translations.uiUtilities.minutes,
          this.translations.uiUtilities.minute,
          this.translations.uiUtilities.mins,
          this.translations.uiUtilities.min,
          this.translations.uiUtilities.hours,
          this.translations.uiUtilities.hour,
          this.translations.uiUtilities.hrs,
          this.translations.uiUtilities.hr,
          this.translations.uiUtilities.days,
          this.translations.uiUtilities.day,
          this.translations.uiUtilities.weeks,
          this.translations.uiUtilities.week,
          this.translations.uiUtilities.months,
          this.translations.uiUtilities.month,
          this.translations.uiUtilities.years,
          this.translations.uiUtilities.year,
          this.translations.uiUtilities.ago,
          this.translations.uiUtilities.updated,
          this.translations.uiUtilities.updatedJustNow,
          this.translations.uiUtilities.justNow,
          this.translations.uiUtilities.lastUpdated,
          this.translations.uiUtilities.on,
          this.translations.uiUtilities.missingGpsData,
          this.translations.uiUtilities.connectionIssue,
          this.translations.uiUtilities.zTrak,
          this.translations.uiUtilities.poweredOff,
          this.translations.uiUtilities.idling,
          this.translations.uiUtilities.heading,
          this.translations.uiUtilities.away,
          this.translations.compassDirections.east,
          this.translations.compassDirections.north,
          this.translations.compassDirections.south,
          this.translations.compassDirections.west,
          this.translations.uiUtilities.ztrakBatteryCondition.good,
          this.translations.uiUtilities.ztrakBatteryCondition.low,
          this.translations.uiUtilities.ztrakBatteryCondition.criticallyLow,
          this.translations.uiUtilities.minuteAgoText,
          this.translations.uiUtilities.minutesAgoText,
          this.translations.uiUtilities.hourAgoText,
          this.translations.uiUtilities.hoursAgoText,
          this.translations.uiUtilities.dayAgoText,
          this.translations.uiUtilities.daysAgoText,
          this.translations.uiUtilities.weekAgoText,
          this.translations.uiUtilities.weeksAgoText,
          this.translations.uiUtilities.monthAgoText,
          this.translations.uiUtilities.monthsAgoText,
          this.translations.uiUtilities.yearAgoText,
          this.translations.uiUtilities.yearsAgoText
        ]);
        this.zTrakBatteryTranslations = {
          GOOD: this.translated[this.translations.uiUtilities.ztrakBatteryCondition.good],
          LOW: this.translated[this.translations.uiUtilities.ztrakBatteryCondition.low],
          CRITICALLY_LOW: this.translated[this.translations.uiUtilities.ztrakBatteryCondition.criticallyLow]
        };
        this.initCompassStrings();
      });
    // this.unitMetric = this.localization.getUseMetricDistance() ? DISTANCE_UNITS.METRIC : DISTANCE_UNITS.IMPERIAL;
  }
  isDefaultLanguage: boolean = this.getLocaleSevice.isDefaultLanguage;
  translated;
  private zTrakBatteryTranslations;
  unitMetric: DISTANCE_UNITS;
  compassDirections;
  compassDirectionsInitialized = false;
  settings: Settings;

  onDestroy$ = new Subject<boolean>();
  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  initCompassStrings() {
    if (this.compassDirectionsInitialized) {
      return;
    }

    const N = this.translated[this.translations.compassDirections.north];
    const S = this.translated[this.translations.compassDirections.south];
    const E = this.translated[this.translations.compassDirections.east];
    const W = this.translated[this.translations.compassDirections.west];
    this.compassDirections = [
      N,
      `${N}${N}${E}`,
      `${N}${E}`,
      `${E}${N}${E}`,
      E,
      `${E}${S}${E}`,
      `${S}${E}`,
      `${S}${S}${E}`,
      S,
      `${S}${S}${W}`,
      `${S}${W}`,
      `${W}${S}${W}`,
      W,
      `${W}${N}${W}`,
      `${N}${W}`,
      `${N}${N}${W}`
    ];
    this.compassDirectionsInitialized = true;
  }

  getTimeAgoString(timestamp: string, verbose: boolean): string {
    if (!timestamp) {
      return '';
    }

    const now = moment();
    const eventTs = moment(timestamp);
    const minutesAgo = now.diff(eventTs, 'minutes');
    const hoursAgo = now.diff(eventTs, 'hours');
    const daysAgo = now.diff(eventTs, 'days');
    const weeksAgo = now.diff(eventTs, 'weeks');
    const monthsAgo = now.diff(eventTs, 'months');
    const yearsAgo = now.diff(eventTs, 'years');
    let isPluralUnits = false;

    if (yearsAgo > 0) {
      isPluralUnits = yearsAgo !== 1;
      return `${verbose ? `${this.translated[this.translations.uiUtilities.updated]} ` : ''}${
        isPluralUnits
          ? this.translated[this.translations.uiUtilities.yearsAgoText].replace('#', yearsAgo)
          : this.translated[this.translations.uiUtilities.yearAgoText].replace('#', yearsAgo)
      }`;
    }

    if (monthsAgo > 0) {
      isPluralUnits = monthsAgo !== 1;
      return `${verbose ? `${this.translated[this.translations.uiUtilities.updated]} ` : ''}${
        isPluralUnits
          ? this.translated[this.translations.uiUtilities.monthsAgoText].replace('#', monthsAgo)
          : this.translated[this.translations.uiUtilities.monthAgoText].replace('#', monthsAgo)
      }`;
    }

    if (weeksAgo > 0) {
      isPluralUnits = weeksAgo !== 1;
      return `${verbose ? `${this.translated[this.translations.uiUtilities.updated]} ` : ''}${
        isPluralUnits
          ? this.translated[this.translations.uiUtilities.weeksAgoText].replace('#', weeksAgo)
          : this.translated[this.translations.uiUtilities.weekAgoText].replace('#', weeksAgo)
      }`;
    }

    if (daysAgo > 0) {
      isPluralUnits = daysAgo !== 1;
      return `${verbose ? `${this.translated[this.translations.uiUtilities.updated]} ` : ''}${
        isPluralUnits
          ? this.translated[this.translations.uiUtilities.daysAgoText].replace('#', daysAgo)
          : this.translated[this.translations.uiUtilities.dayAgoText].replace('#', daysAgo)
      }`;
    }

    if (hoursAgo > 0) {
      isPluralUnits = hoursAgo !== 1;
      return `${verbose ? `${this.translated[this.translations.uiUtilities.updated]} ` : ''}${
        isPluralUnits
          ? this.translated[this.translations.uiUtilities.hoursAgoText].replace('#', hoursAgo)
          : this.translated[this.translations.uiUtilities.hourAgoText].replace('#', hoursAgo)
      }`;
    }

    if (minutesAgo <= 0) {
      return verbose
        ? this.translated[this.translations.uiUtilities.updatedJustNow]
        : this.translated[this.translations.uiUtilities.justNow];
    }

    if (minutesAgo > 0) {
      isPluralUnits = minutesAgo !== 1;
      return `${verbose ? `${this.translated[this.translations.uiUtilities.updated]} ` : ''}${
        isPluralUnits
          ? this.translated[this.translations.uiUtilities.minutesAgoText].replace('#', minutesAgo)
          : this.translated[this.translations.uiUtilities.minuteAgoText].replace('#', minutesAgo)
      }`;
    }
  }

  getTimeAgoStringForDetailPage(
    timestamp: string,
    verbose: boolean,
    timeFormat = 'h:mm A',
    dateFormat = 'MM/DD/YY'
  ): string {
    if (!timestamp) {
      return '';
    }
    let { timeZone } = this.appConfigService.getConfig();
    timeZone = timeZone || this.appConfigService.getLocalTimeZone();
    const now = moment();
    //const eventTs = moment(timestamp);
    const eventTs = moment.tz(timestamp, timeZone);
    const minutesAgo = now.diff(eventTs, 'minutes');
    if (minutesAgo > 60) {
      const verboseFormat = `[${this.translated[this.translations.uiUtilities.lastUpdated]}] [${
        this.translated[this.translations.uiUtilities.on]
      }] ${dateFormat}, ${timeFormat}`;
      const terseFormat = `${timeFormat} [${this.translated[this.translations.uiUtilities.on]}] ${dateFormat}`;
      return eventTs.format(verbose ? verboseFormat : terseFormat);
    }

    if (minutesAgo <= 0) {
      return verbose
        ? this.translated[this.translations.uiUtilities.updatedJustNow]
        : this.translated[this.translations.uiUtilities.justNow];
    }

    const isPluralUnits = minutesAgo !== 1;
    return `${verbose ? `${this.translated[this.translations.uiUtilities.updated]} ` : ''}${
      isPluralUnits
        ? this.translated[this.translations.uiUtilities.minutesAgoText].replace('#', minutesAgo)
        : this.translated[this.translations.uiUtilities.minuteAgoText].replace('#', minutesAgo)
    }`;
  }

  metersToMiles(meters): number {
    return meters ? meters * MILES_PER_METER : 0;
  }

  metersToKms(meters): number {
    return meters ? meters * KM_PER_METER : 0;
  }

  mmPerSecToMph(speed): number {
    // convert asset speed in mm/sec to mph rounded to nearest whole value
    return speed * MPH_PER_MM_SEC;
  }

  mmPerSecToKph(speed): number {
    return speed * KPH_PER_MM_SEC;
  }

  kmhToMph(kmh) {
    return kmh * MILES_PER_KM_H;
  }

  convertSpeed(speed): number | string {
    // Given that speed from live-data events is in format km/h
    const convertedSpeed = this.settings.speedUnit === PathSpeedUnits.MPH ? this.kmhToMph(speed) : speed;
    return !Number.isNaN(convertedSpeed) ? Math.round(convertedSpeed) : '???';
  }

  formatSpeed(speed): string {
    const units = this.getSpeedUnit();
    const convertedSpeed = this.convertSpeed(speed);
    return `${convertedSpeed} ${units.toLowerCase()}`;
  }

  convertDistance(meters): number {
    // Default distance should be km, defined in MAPS-1558
    if (this.isDefaultLanguage) return this.metersToKms(meters);
    //return this.unitMetric == DISTANCE_UNITS.IMPERIAL ? this.metersToMiles(meters) : this.metersToKms(meters);
    return this.settings.distanceUnit === PathDistanceUnits.MI ? this.metersToMiles(meters) : this.metersToKms(meters);
  }

  getSpeedUnit(): PathSpeedUnits {
    return this.settings.speedUnit === PathSpeedUnits.MPH ? PathSpeedUnits.MPH : PathSpeedUnits.KPH;
  }

  getDistanceUnit(): string {
    // Default distance should be km, defined in MAPS-1558
    if (this.isDefaultLanguage) return PathDistanceUnits.KM;
    // return this.unitMetric == DISTANCE_UNITS.IMPERIAL ? PathDistanceUnits.MI : PathDistanceUnits.KM;
    return this.settings.distanceUnit === PathDistanceUnits.MI ? PathDistanceUnits.MI : PathDistanceUnits.KM;
  }

  assetSubtitle(asset) {
    if (!validGpsData(asset)) {
      return this.translated[this.translations.uiUtilities.missingGpsData];
    }
    if (asset?.connectionIssue) {
      return this.translated[this.translations.uiUtilities.connectionIssue];
    }
    //if (asset?.legacyAttributes?.gpssn && this.gpssnToGpsType(asset?.legacyAttributes?.gpssn) === DeviceType.ZTRAK) {
    //  return this.translated[this.translations.uiUtilities.zTrak];
    //}
    if (!asset.powerOn) {
      return this.translated[this.translations.uiUtilities.poweredOff];
    }
    if (asset.powerOn && asset.idling) {
      return this.translated[this.translations.uiUtilities.idling];
    }
    // powered on and not idling, we're in motion
    return `${this.translated[this.translations.uiUtilities.heading]} ${this.degToCompass(asset.heading)} ${
      this.settings.showVehicleSpeed ? `- ${this.formatSpeed(asset.speed)}` : ''
    }`;
  }

  assetSidebarMessage(distance: number): string {
    //const units = this.unitMetric === DISTANCE_UNITS.IMPERIAL ? DistanceUnits.MILE : DistanceUnits.KM;
    const units = this.settings.distanceUnit === PathDistanceUnits.MI ? DistanceUnits.MILE : DistanceUnits.KM;
    return `${distance >= 10 ? distance?.toFixed(0) : distance?.toFixed(1)} ${units}. ${
      this.translated[this.translations.uiUtilities.away]
    }`;
  }

  formatDuration(milliseconds, trim = true): string {
    const durationTemplate =
      milliseconds / 1000 / 60 > 60
        ? `h [${this.translated[this.translations.uiUtilities.hrs]}], m [${
            this.translated[this.translations.uiUtilities.mins]
          }]`
        : `m [${this.translated[this.translations.uiUtilities.mins]}]`;
    return moment.duration(milliseconds, 'milliseconds').format(durationTemplate, { trim: trim ? 'all' : 'large' });
  }

  formatDistance(meters): string {
    const units = this.getDistanceUnit();
    const distance = Math.round(this.convertDistance(meters));
    let suffix;
    if (distance === 1) {
      suffix = units === PathDistanceUnits.MI ? 'mile' : 'km';
    } else {
      suffix = units === PathDistanceUnits.MI ? 'miles' : 'kms';
    }
    return `${distance} ${suffix}`;
  }

  formatFuelConsumption(startFuel: number | null, endFuel: number | null, distanceInMeters: number | null): string {
    if (
      startFuel != null &&
      endFuel != null &&
      startFuel != endFuel &&
      distanceInMeters != null &&
      !isNaN(startFuel!) &&
      !isNaN(endFuel!) &&
      !isNaN(distanceInMeters!) &&
      distanceInMeters! > 1000
    ) {
      // const units = this.getDistanceUnit();
      let c = ((endFuel! - startFuel!) * 100000) / distanceInMeters!; // 100000 for 100km
      // if (c > 0 && units === PathDistanceUnits.MI) {
      //   // convert to miles per galon
      //   c = 235.2 / c;
      // }
      let conso = Math.round(c * 10) / 10;
      // if (units === PathDistanceUnits.MI) {
      //  return conso + ' mpg';
      //}
      return conso + ' L/100km';
    }
    return '';
  }

  formatDistanceWithoutSuffix(meters): number {
    const distance = this.convertDistance(meters);
    return distance;
  }
  gpssnToGpsType(gpssn: string | number) {
    const gpsSnToTypeMap = [
      { low: 1000, high: 1007472, type: 'HDGPS' },
      { low: 2000000, high: 2999999, type: 'V2, V2J' },
      { low: 3000000, high: 3999999, type: 'V2J (FGA)' },
      { low: 4000000, high: 4319999, type: 'V2' },
      { low: 4320000, high: 4499999, type: 'V2, high: K12' },
      { low: 4500000, high: 4999999, type: 'K12J' },
      { low: 5000000, high: 5999999, type: 'V2J' },
      { low: 6000000, high: 6999999, type: 'VT ECU' },
      { low: 7000000, high: 7009999, type: 'Wombat V2' },
      { low: 7010000, high: 7499999, type: 'V3R' },
      { low: 8000000, high: 8999999, type: 'V3' },
      { low: 11000000, high: 11999999, type: 'VTHU' },
      { low: 12000000, high: 12999999, type: 'V3i' },
      { low: 13000000, high: 13200000, type: 'VT 500' },
      { low: 14000000, high: 14010000, type: 'V3 500' },
      { low: 17000000, high: 17999999, type: 'VTHU' },
      { low: 18000000, high: 18999999, type: 'V4' },
      { low: 21000000, high: 21999999, type: 'V4' },
      { low: 22000000, high: 22999999, type: 'VT4' },
      { low: 23000000, high: 23999999, type: 'VT4 FSA' },
      { low: 25000000, high: 25999999, type: 'cTP' },
      { low: 26000000, high: 26999999, type: 'V4R' },
      { low: 27000000, high: 27999999, type: 'V4 Base' },
      { low: 30000000, high: 30999999, type: 'V4 Essential' },
      { low: 33000000, high: 33999999, type: 'V4C' },
      { low: 35000000, high: 35099999, type: 'Navistar' },
      { low: 3000000000, high: 3999999999, type: 'zTrak' },
      // These are actually AT22, but we will still display 'zTrak' to the user
      { low: 350457790000000, high: 350457799999999, type: 'zTrak' },
      { low: 351358810000000, high: 351358819999999, type: 'zTrak' },
      { low: 351516170000000, high: 351516179999999, type: 'zTrak' },
      { low: 351901930000000, high: 351901939999999, type: 'zTrak' },
      { low: 4000000000, high: 4000010000, type: 'Zonar R&D' },
      { low: 16042000000000, high: 16042009999999, type: 'LD TCU' },
      { low: 353731110000000, high: 353731119999999, type: 'LD TCU' },
      { low: 353732110000000, high: 353732119999999, type: 'LD TCU' },
      { low: 860264050000000, high: 867648049999999, type: 'RDL 4G' }
    ];

    if (!gpssn) {
      return 'UNKNOWN';
    }
    // const regex = /\d{4}\.[0-1]{10}\d{9}B[0-1]{7}\d/;
    // if (regex.test(gpssn.toString())) {
    //   return 'VDO Link';
    // }
    const regex = /^1982\d{7}$/;
    if (regex.test(gpssn.toString())) {
      return 'VDO Link';
    }
    const ssnAsNumber = +gpssn;
    for (const gpsType of gpsSnToTypeMap) {
      if (ssnAsNumber >= gpsType.low && ssnAsNumber <= gpsType.high) {
        return gpsType.type;
      }
    }

    return 'UNKNOWN';
  }

  isAssetTracker(gpssn: string | number) {
    const ssnAsNumber = +gpssn;
    return (
      (ssnAsNumber >= 350457790000000 && ssnAsNumber <= 350457799999999) ||
      (ssnAsNumber >= 351358810000000 && ssnAsNumber <= 351358819999999) ||
      (ssnAsNumber >= 351516170000000 && ssnAsNumber <= 351516179999999) ||
      (ssnAsNumber >= 351901930000000 && ssnAsNumber <= 351901939999999)
    );
  }

  getTranslatedBatteryCondition(ztrakBatteryCondition: number): string {
    return this.zTrakBatteryTranslations[ZTRAK_BATTERY_CONDITION[ztrakBatteryCondition]];
  }

  degToCompass(heading) {
    // convert heading (100ths of deg) to 1 of 16 cardinal directions. Compass is treated as 16, 22.5 degree
    // wide slices centered around the 16 cardinal directions. For example, a heading of 60 degrees would
    // fall into the ENE slice because it is within +/- 11.25 degrees of 67.5 degrees (ENE).
    return this.compassDirections[Math.round(heading / 22.5) % 16];
  }

  getCompassforIcon(heading) {
    const compassIcon = Math.round(heading / 22.5) % 16;
    switch (compassIcon) {
      case 0:
        return 'N';
        break;
      case 1:
        return 'NNE';
        break;
      case 2:
        return 'NE';
        break;
      case 3:
        return 'ENE';
        break;
      case 4:
        return 'E';
        break;
      case 5:
        return 'ESE';
        break;
      case 6:
        return 'SE';
        break;
      case 7:
        return 'SSE';
        break;
      case 8:
        return 'S';
        break;
      case 9:
        return 'SSW';
        break;
      case 10:
        return 'SW';
        break;
      case 11:
        return 'WSW';
        break;
      case 12:
        return 'W';
        break;
      case 13:
        return 'WNW';
        break;
      case 14:
        return 'NW';
        break;
      case 15:
        return 'NNW';
        break;
    }
  }

  getAddressText(geocodeData: ReverseGeocoderData) {
    return geocodeData.address || 'Address Unknown';
  }

  convertLuxonToMomentFormat(luxonFormat) {
    const luxonToMomentMap = {
      yyyy: 'YYYY',
      yy: 'YY',
      MM: 'MM',
      dd: 'DD',
      d: 'D',
      HH: 'HH',
      H: 'H',
      mm: 'mm',
      m: 'm',
      ss: 'ss',
      s: 's',
      a: 'A'
    };
    return luxonFormat?.replace(/yyyy|yy|MM|dd|d|HH|H|mm|m|ss|s|a/g, match => luxonToMomentMap[match] || match);
  }
}
